// Platform configuration registry
import React from 'react';
import { ShopifyMapping } from './ShopifyMapping';
import { MagentoMapping } from './MagentoMapping';
import { BigCommerceMapping } from './BigCommerceMapping';

export interface PlatformField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'url' | 'select';
  required: boolean;
  placeholder?: string;
  description?: string;
  options?: Array<{ value: string; label: string }>;
}

export interface PlatformInfo {
  value: string;
  label: string;
  description: string;
  icon: string | React.ReactElement;
  fields: PlatformField[];
  mappingComponent?: React.ComponentType<any>;
  supportedFeatures: string[];
  apiDocs?: string;
}

// Base mapping interface that all platforms should implement
export interface BaseMappingProps {
  form: any;
  attributes: any[];
  priceTypes: any[];
  selectedPlatform: string;
}

// Function to generate platform configurations with translations
export function generatePlatforms(platformTranslations?: any): PlatformInfo[] {
  const platforms = platformTranslations?.platforms;
  const supportedFeatures = platformTranslations?.supportedFeatures;

  // Helper function to translate supported features
  const translateFeatures = (features: string[]) => {
    if (!supportedFeatures) return features;
    return features.map(feature => supportedFeatures[feature] || feature);
  };

  return [
    {
      value: 'shopify',
      label: platforms?.shopify?.label || '',
      description: platforms?.shopify?.description || '',
      icon: React.createElement('img', {
        src: '/connectoricon/shopify.png',
        alt: 'Shopify',
        className: 'w-8 h-8 object-contain',
      }),
      apiDocs: 'https://shopify.dev/docs/api/admin-rest/2023-10/resources/product',
      mappingComponent: ShopifyMapping,
      supportedFeatures: translateFeatures([
        'products',
        'variants',
        'images',
        'categories',
        'inventory',
        'pricing',
        'seo',
        'metafields',
        'options',
      ]),
      fields: [
        {
          key: 'shop_domain',
          label: platforms?.shopify?.fields?.shopDomain?.label || '',
          type: 'text' as const,
          required: true,
          placeholder: platforms?.shopify?.fields?.shopDomain?.placeholder || '',
          description: platforms?.shopify?.fields?.shopDomain?.description || '',
        },
        {
          key: 'access_token',
          label: platforms?.shopify?.fields?.accessToken?.label || '',
          type: 'password' as const,
          required: true,
          placeholder: platforms?.shopify?.fields?.accessToken?.placeholder || '',
          description: platforms?.shopify?.fields?.accessToken?.description || '',
        },
        {
          key: 'api_version',
          label: platforms?.shopify?.fields?.apiVersion?.label || '',
          type: 'select' as const,
          required: false,
          description: platforms?.shopify?.fields?.apiVersion?.description || '',
          options: [
            {
              value: '2025-04',
              label: platforms?.shopify?.fields?.apiVersion?.options?.['2025-04'] || '2025-04',
            },
            {
              value: '2025-01',
              label: platforms?.shopify?.fields?.apiVersion?.options?.['2025-01'] || '2025-01',
            },
            {
              value: '2024-10',
              label: platforms?.shopify?.fields?.apiVersion?.options?.['2024-10'] || '2024-10',
            },
            {
              value: '2024-01',
              label: platforms?.shopify?.fields?.apiVersion?.options?.['2024-01'] || '2024-01',
            },
          ],
        },
      ],
    },
    {
      value: 'magento',
      label: platforms?.magento?.label || '',
      description: platforms?.magento?.description || '',
      icon: React.createElement('img', {
        src: '/connectoricon/magento.png',
        alt: 'Magento',
        className: 'w-8 h-8 object-contain',
      }),
      apiDocs: 'https://devdocs.magento.com/guides/v2.4/rest/bk-rest.html',
      mappingComponent: MagentoMapping,
      supportedFeatures: translateFeatures([
        'products',
        'categories',
        'inventory',
        'pricing',
        'attributes',
      ]),
      fields: [
        {
          key: 'base_url',
          label: platforms?.magento?.fields?.baseUrl?.label || '',
          type: 'url' as const,
          required: true,
          placeholder: platforms?.magento?.fields?.baseUrl?.placeholder || '',
          description: platforms?.magento?.fields?.baseUrl?.description || '',
        },
        {
          key: 'consumer_key',
          label: platforms?.magento?.fields?.consumerKey?.label || '',
          type: 'text' as const,
          required: true,
          placeholder: platforms?.magento?.fields?.consumerKey?.placeholder || '',
          description: platforms?.magento?.fields?.consumerKey?.description || '',
        },
        {
          key: 'consumer_secret',
          label: platforms?.magento?.fields?.consumerSecret?.label || '',
          type: 'password' as const,
          required: true,
          placeholder: platforms?.magento?.fields?.consumerSecret?.placeholder || '',
          description: platforms?.magento?.fields?.consumerSecret?.description || '',
        },
        {
          key: 'access_token',
          label: platforms?.magento?.fields?.accessToken?.label || '',
          type: 'password' as const,
          required: true,
          placeholder: platforms?.magento?.fields?.accessToken?.placeholder || '',
          description: platforms?.magento?.fields?.accessToken?.description || '',
        },
        {
          key: 'access_token_secret',
          label: platforms?.magento?.fields?.accessTokenSecret?.label || '',
          type: 'password' as const,
          required: true,
          placeholder: platforms?.magento?.fields?.accessTokenSecret?.placeholder || '',
          description: platforms?.magento?.fields?.accessTokenSecret?.description || '',
        },
      ],
    },
    {
      value: 'bigcommerce',
      label: platforms?.bigcommerce?.label || '',
      description: platforms?.bigcommerce?.description || '',
      icon: React.createElement('img', {
        src: '/connectoricon/bigcommerce.png',
        alt: 'BigCommerce',
        className: 'w-8 h-8 object-contain',
      }),
      apiDocs: 'https://developer.bigcommerce.com/docs/rest-catalog/products',
      mappingComponent: BigCommerceMapping,
      supportedFeatures: translateFeatures([
        'products',
        'variants',
        'images',
        'categories',
        'inventory',
        'pricing',
      ]),
      fields: [
        {
          key: 'store_hash',
          label: platforms?.bigcommerce?.fields?.storeHash?.label || '',
          type: 'text' as const,
          required: true,
          placeholder: platforms?.bigcommerce?.fields?.storeHash?.placeholder || '',
          description: platforms?.bigcommerce?.fields?.storeHash?.description || '',
        },
        {
          key: 'access_token',
          label: platforms?.bigcommerce?.fields?.accessToken?.label || '',
          type: 'password' as const,
          required: true,
          placeholder: platforms?.bigcommerce?.fields?.accessToken?.placeholder || '',
          description: platforms?.bigcommerce?.fields?.accessToken?.description || '',
        },
      ],
    },
  ];
}

// Static platform definitions for backwards compatibility
export const PLATFORMS: PlatformInfo[] = generatePlatforms();

// Helper function to get platform info
export function getPlatformInfo(platformKey: string): PlatformInfo | undefined {
  return PLATFORMS.find(p => p.value === platformKey);
}

// Helper function to get all platform keys
export function getAllPlatformKeys(): string[] {
  return PLATFORMS.map(p => p.value);
}

// Helper function to check if platform supports feature
export function platformSupportsFeature(platformKey: string, feature: string): boolean {
  const platform = getPlatformInfo(platformKey);
  return platform?.supportedFeatures.includes(feature) ?? false;
}

// Export all mapping components
export { ShopifyMapping } from './ShopifyMapping';
export { MagentoMapping } from './MagentoMapping';
export { BigCommerceMapping } from './BigCommerceMapping';
export { BaseConnectorMapping } from './BaseConnectorMapping';
